import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Truck, 
  Package, 
  Clock, 
  CheckCircle, 
  MapPin,
  Phone,
  Calendar,
  IndianRupee,
  ToggleLeft,
  ToggleRight,
  Navigation,
  Store,
  User,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeliveryDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [isAvailable, setIsAvailable] = useState(user?.is_available !== false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'completed', 'all'
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
    // Fetch current availability status
    fetchAvailability();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      // Filter orders assigned to this delivery agent
      const myOrders = response.data.filter(o => o.delivery_agent_id === user.id);
      setOrders(myOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
      setIsAvailable(response.data.is_available !== false);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const toggleAvailability = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/delivery-agent/availability`,
        { is_available: !isAvailable },
        { headers: getAuthHeader() }
      );
      setIsAvailable(!isAvailable);
      toast.success(t('availabilityUpdated'));
    } catch (error) {
      toast.error(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(t('statusUpdated'));
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error(t('loadFailed'));
    }
  };

  const getStats = () => {
    const total = orders.length;
    const active = orders.filter(o => ['assigned', 'picked_up'].includes(o.status)).length;
    const completed = orders.filter(o => o.status === 'delivered').length;
    const todayDeliveries = orders.filter(o => {
      const orderDate = new Date(o.created_at).toDateString();
      const today = new Date().toDateString();
      return orderDate === today && o.status === 'delivered';
    }).length;
    const earnings = orders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total_amount * 0.1), 0); // 10% commission
    return { total, active, completed, todayDeliveries, earnings };
  };

  const stats = getStats();

  const statusConfig = {
    assigned: { 
      color: 'bg-blue-100 text-blue-800', 
      icon: Truck, 
      label: t('assigned'),
      nextStatus: 'picked_up',
      nextLabel: t('markPickedUp')
    },
    picked_up: { 
      color: 'bg-purple-100 text-purple-800', 
      icon: Package, 
      label: t('pickedUp'),
      nextStatus: 'delivered',
      nextLabel: t('markDelivered')
    },
    delivered: { 
      color: 'bg-green-100 text-green-800', 
      icon: CheckCircle, 
      label: t('delivered'),
      nextStatus: null,
      nextLabel: null
    }
  };

  const getFilteredOrders = () => {
    switch (activeTab) {
      case 'active':
        return orders.filter(o => ['assigned', 'picked_up'].includes(o.status));
      case 'completed':
        return orders.filter(o => o.status === 'delivered');
      default:
        return orders;
    }
  };

  const filteredOrders = getFilteredOrders();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header with Availability Toggle */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-bold text-3xl md:text-4xl text-gray-900 mb-2">
              {t('deliveryDashboard')}
            </h1>
            <p className="text-gray-600">{t('manageDeliveries')}</p>
          </div>
          
          {/* Availability Toggle */}
          <div className="mt-4 md:mt-0">
            <div 
              className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                isAvailable 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-100 border-gray-200'
              }`}
              onClick={toggleAvailability}
              data-testid="availability-toggle"
            >
              <div className={`w-14 h-8 rounded-full relative transition-all ${
                isAvailable ? 'bg-green-500' : 'bg-gray-400'
              }`}>
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all ${
                  isAvailable ? 'right-1' : 'left-1'
                }`} />
              </div>
              <div>
                <p className={`font-bold ${isAvailable ? 'text-green-700' : 'text-gray-600'}`}>
                  {isAvailable ? t('available') : t('notAvailable')}
                </p>
                <p className="text-xs text-gray-500">
                  {isAvailable ? t('canReceiveOrders') : t('cannotReceiveOrders')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">{t('activeDeliveries')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.active}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">{t('completedDeliveries')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-600">{t('today')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.todayDeliveries}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalOrders')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-gray-600">{t('earnings')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">₹{stats.earnings.toFixed(0)}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'active' 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-active"
          >
            <Truck className="w-5 h-5 inline mr-2" />
            {t('activeDeliveries')} ({stats.active})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'completed' 
                ? 'bg-green-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-completed"
          >
            <CheckCircle className="w-5 h-5 inline mr-2" />
            {t('completedDeliveries')} ({stats.completed})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'all' 
                ? 'bg-gray-800 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-all"
          >
            <Package className="w-5 h-5 inline mr-2" />
            {t('all')} ({stats.total})
          </button>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-bold text-xl text-gray-900 mb-2">
              {activeTab === 'active' ? t('noActiveDeliveries') : t('noOrdersYet')}
            </h3>
            <p className="text-gray-500">
              {activeTab === 'active' && isAvailable 
                ? t('waitForOrders')
                : activeTab === 'active' && !isAvailable
                ? t('turnOnAvailability')
                : t('deliveryHistory')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.assigned;
              const StatusIcon = status.icon;
              return (
                <div 
                  key={order.id} 
                  className={`bg-white rounded-2xl border-2 p-6 shadow-sm hover:shadow-md transition-all ${
                    order.status === 'assigned' ? 'border-blue-200' : 
                    order.status === 'picked_up' ? 'border-purple-200' : 
                    'border-gray-100'
                  }`}
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                          <StatusIcon size={16} />
                          {status.label}
                        </span>
                        <span className="text-sm text-gray-500">#{order.id.slice(0, 8)}</span>
                      </div>
                      
                      {/* Pickup & Delivery Info */}
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Store size={16} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-blue-600 font-medium">{t('pickupAddress')}</p>
                            <p className="text-sm text-gray-900">{t('shopLocation')}</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-center">
                          <div className="w-0.5 h-4 bg-gray-200"></div>
                        </div>
                        
                        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <MapPin size={16} className="text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-green-600 font-medium">{t('deliveryAddressLabel')}</p>
                            <p className="text-sm text-gray-900">{order.delivery_address}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Package size={14} />
                          {order.items.length} {t('items')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <p className="font-bold text-2xl text-gray-900">₹{order.total_amount}</p>
                        <p className="text-xs text-gray-500">{t('cashOnDelivery')}</p>
                      </div>
                      
                      {status.nextStatus && (
                        <Button
                          onClick={() => updateOrderStatus(order.id, status.nextStatus)}
                          className={`rounded-full px-6 ${
                            order.status === 'assigned' 
                              ? 'bg-purple-500 hover:bg-purple-600' 
                              : 'bg-green-500 hover:bg-green-600'
                          } text-white`}
                          data-testid={`update-status-${order.id}`}
                        >
                          <ArrowRight size={16} className="mr-2" />
                          {status.nextLabel}
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => setSelectedOrder(order)}
                      >
                        {t('details')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Order Details Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('orderDetails')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('orderId')}:</span>
                  <span className="font-mono">#{selectedOrder.id.slice(0, 8)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('status')}:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[selectedOrder.status]?.color}`}>
                    {statusConfig[selectedOrder.status]?.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('date')}:</span>
                  <span>{new Date(selectedOrder.created_at).toLocaleString('en-IN')}</span>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">{t('orderItems')}</h4>
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                      <span>{item.product_name} x {item.quantity}</span>
                      <span className="font-semibold">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-bold text-lg">{t('total')}:</span>
                  <span className="font-bold text-2xl text-emerald-600">₹{selectedOrder.total_amount}</span>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-2">{t('deliveryAddressLabel')}</h4>
                  <p className="text-gray-600 flex items-start gap-2">
                    <MapPin size={16} className="mt-1" />
                    {selectedOrder.delivery_address}
                  </p>
                </div>

                {statusConfig[selectedOrder.status]?.nextStatus && (
                  <Button
                    onClick={() => updateOrderStatus(selectedOrder.id, statusConfig[selectedOrder.status].nextStatus)}
                    className={`w-full rounded-full ${
                      selectedOrder.status === 'assigned' 
                        ? 'bg-purple-500 hover:bg-purple-600' 
                        : 'bg-green-500 hover:bg-green-600'
                    } text-white`}
                  >
                    <ArrowRight size={16} className="mr-2" />
                    {statusConfig[selectedOrder.status].nextLabel}
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DeliveryDashboard;
